import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useFormContext } from 'react-hook-form';
import { percentageFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import SvgChevron from '@app/shared/icons/Chevron';
import SvgRevert from '@app/shared/icons/Revert';
import { FC, Fragment } from 'react';
import styles from '../../opm-inputs.module.scss';
import useLocalStorageCollapseState from '@app/core/hooks/useLocalStoreCollapse';
import { useLocale } from '@app/core/hooks/useLocale';
import { CalcMethod, OpmInputDataSource } from '@app/shared/models/contracts/enums/shared-enums';
import { OpmInputFormModel2 } from '../../OpmInputs';
import {
  getDlomIndexByInstrumentIdForOpm,
  getDlomIndexByTrancheIdForOpm,
} from '@app/shared/helpers/equity-instruments/get-equity-instrument-index';
import { Pill, PillSize, PillTheme } from '@app/shared/components/pill/Pill';
import { PerYearValuesDto } from '@app/shared/models/contracts/project-dto';
import { selectCapitalStructure } from '@app/core/store/capital-structure-slice-selectors';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import OpmInputEmptyHistoricCells from '../OpmInputEmptyHistoricCells';
import { useOpmInputsFromPwerm } from '../../utils/useOpmInputsFromPwerm';
import {
  filterHistoricEventDatesFromFirstEvent,
  getMaxColumnCount,
} from '../../utils/OpmInputHelper';
import { minMaxValidator } from '@app/core/validations/hook-forms/validators';

interface MinorityDiscountsSectionProps {
  submitData: () => void;
}

const MinorityDiscountsSection: FC<MinorityDiscountsSectionProps> = ({
  submitData,
}): JSX.Element => {
  const project = useAppSelector((state) => state.project.projectDraft);
  const isOPMOnly = project.details.calcMethod === CalcMethod.OPM;
  const opmInput = project.opmInput;
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const filteredInstruments = Object.entries(capitalStructure.instrumentDefinitions)
    .map(([instrumentId, instrument]) => {
      return {
        ...instrument,
        instrumentId,
      };
    })
    .filter((instrument) => instrument.shouldBeValued);
  const sortedFilteredInstruments = useInstrumentSort(filteredInstruments);
  const { l } = useLocale();
  const formMethods = useFormContext<OpmInputFormModel2>();

  const [expanded, setExpanded] = useLocalStorageCollapseState(
    'expanded-minority-discount',
    project.id
  );

  const toggleCollapse = () => {
    setExpanded(!expanded);
  };
  const opmInputResults = useAppSelector((state) => state.opmCalculation.calculatedInputs);
  const showHistoricDataColumns = useAppSelector(
    (state) => state.uiValues.userSelections.opmInputs.showHistoricDataColumns
  );
  const historicColumnCount = Object.keys(
    filterHistoricEventDatesFromFirstEvent(opmInputResults?.historicEventDates ?? [])
  ).length;
  const perYearInputs = project.opmInput.perYearInputs;
  const opmPwermValues = useOpmInputsFromPwerm();
  const shouldGetValuesFromPwerm = project.details.calcMethod !== CalcMethod.OPM;
  const maxColspanCount =
    project.opmInput.forecastYears !== null
      ? getMaxColumnCount(
          historicColumnCount,
          project.opmInput.forecastYears,
          showHistoricDataColumns
        )
      : 0;
  const maxDataColspanCount = maxColspanCount - 1;

  const isMinorityDiscountsTrancheOverridden = perYearInputs.some((pyi) => {
    return pyi.marketValueDloms?.some((item) => {
      return (
        item.trancheDloms &&
        item.trancheDloms.some((item1) => {
          return item1.dlomSource === OpmInputDataSource.Override;
        })
      );
    });
  });

  const minorityDiscountsSource = perYearInputs.some((pyi) => {
    return pyi.marketValueDloms?.some((item) => {
      return (
        item.instrumentDlomSource === OpmInputDataSource.Override ||
        isMinorityDiscountsTrancheOverridden
      );
    });
  })
    ? OpmInputDataSource.Override
    : OpmInputDataSource.FromPWERM;

  const displayInstrumentRevertForYear = (yearInputs: PerYearValuesDto, instrumentId: string) => {
    if (shouldGetValuesFromPwerm) {
      const marketValueDloms = yearInputs.marketValueDloms.find(
        (mvd) => mvd.instrumentId === instrumentId
      );
      return marketValueDloms?.instrumentDlomSource === OpmInputDataSource.Override;
    }

    return false;
  };

  const displayInstrumentRevert = (instrumentId: string) => {
    if (shouldGetValuesFromPwerm) {
      return perYearInputs?.some((yi) => {
        return displayInstrumentRevertForYear(yi, instrumentId);
      });
    }
  };

  const displayInstrumentDlocRevert = (instrumentId: string) => {
    if (shouldGetValuesFromPwerm) {
      return (
        opmInput.controlPremiumForDlocByInstrumentSource?.[instrumentId] ===
        OpmInputDataSource.Override
      );
    }
    return false;
  };

  const handleInstrumentDlocRevert = (instrumentId: string) => {
    const valueToSet = opmPwermValues.getControlPremiumForDlocByInstrumentFromPwerm()[instrumentId];
    formMethods.setValue(`controlPremiumForDlocByInstrument.${instrumentId}`, valueToSet);
    submitData();
  };

  const handleInstrumentRevert = (instrumentId: string) => {
    perYearInputs.forEach((yi, index) => {
      const valueToSet = opmPwermValues
        .getOpmValuesFromPwerm(yi.forecastDate)
        .marketValueDloms.find((item) => item.instrumentId === instrumentId)?.instrumentDlom;
      const valueIndex = opmPwermValues
        .getOpmValuesFromPwerm(yi.forecastDate)
        .marketValueDloms.findIndex((item) => item.instrumentId === instrumentId);
      formMethods.setValue(
        `perYearInputs.${index}.marketValueDloms.${valueIndex}.instrumentDlom`,
        valueToSet ?? 0
      );
    });
    submitData();
  };

  const displayTrancheRevertForYear = (
    yearInputs: PerYearValuesDto,
    instrumentId: string,
    trancheId: string
  ) => {
    if (shouldGetValuesFromPwerm) {
      const marketValueDloms = yearInputs.marketValueDloms.find(
        (mvd) => mvd.instrumentId === instrumentId
      );
      const canRevert =
        marketValueDloms?.trancheDloms.find((td) => td.trancheId === trancheId)?.dlomSource ===
        OpmInputDataSource.Override;
      return canRevert;
    }

    return false;
  };

  const displayTrancheRevert = (instrumentId: string, trancheId: string) => {
    if (shouldGetValuesFromPwerm) {
      return perYearInputs.some((yi) => displayTrancheRevertForYear(yi, instrumentId, trancheId));
    }
  };

  const handleTrancheRevert = (instrumentId: string, trancheId: string) => {
    perYearInputs.forEach((yi, index) => {
      const valueToSet = opmPwermValues
        .getOpmValuesFromPwerm(yi?.forecastDate)
        ?.marketValueDloms.find((item) => item.instrumentId === instrumentId)
        ?.trancheDloms.find((item) => item.trancheId === trancheId)?.dlom;

      const instrumentValueIndex = opmPwermValues
        .getOpmValuesFromPwerm(yi?.forecastDate)
        ?.marketValueDloms.findIndex((item) => item.instrumentId === instrumentId);

      const trancheValueIndex =
        opmPwermValues
          .getOpmValuesFromPwerm(yi?.forecastDate)
          ?.marketValueDloms.find((item) => item?.instrumentId === instrumentId)
          ?.trancheDloms.findIndex((item) => item?.trancheId === trancheId) ?? 0;

      formMethods.setValue(
        `perYearInputs.${index}.marketValueDloms.${instrumentValueIndex}.trancheDloms.${trancheValueIndex}.dlom`,
        valueToSet ?? 0
      );
    });
    submitData();
  };

  const isOverridden = () => {
    const hasDlocOverride =
      opmInput.controlPremiumForDlocByInstrumentSource &&
      Object.values(opmInput.controlPremiumForDlocByInstrumentSource).some(
        (source) =>
          source !== null && source !== undefined && source === OpmInputDataSource.Override
      );

    return minorityDiscountsSource === OpmInputDataSource.Override || Boolean(hasDlocOverride);
  };

  return sortedFilteredInstruments.length > 0 ? (
    <>
      <tbody>
        <tr>
          <th className={`table-primary__cell--header-tertiary ${styles[`opm-header`]}`}>
            <div className={styles['opm-collapsable-header']}>
              <button
                type="button"
                data-testid="md-collapse-button"
                aria-label={l('_ToggleSectionCollapse', {
                  section: l('_MinorityDiscounts'),
                })}
                aria-expanded={expanded}
                style={{
                  border: 'none',
                  backgroundColor: 'none',
                  background: 'none',
                }}
                onClick={toggleCollapse}>
                {!expanded ? (
                  <SvgChevron
                    onClick={toggleCollapse}
                    className={styles[`opm-chevron-collapsed`]}
                  />
                ) : (
                  <SvgChevron onClick={toggleCollapse} className={styles[`opm-chevron-expanded`]} />
                )}
              </button>
              {l('_MinorityDiscounts')}
            </div>
          </th>
          <th
            className={`table-primary__cell--header-tertiary ${styles[`opm-header-badge`]}`}
            colSpan={maxDataColspanCount}>
            {project.details.calcMethod !== CalcMethod.OPM && (
              <div className={styles['opm-collapsable-header-badge']} data-testid="md-badge">
                <Pill
                  size={PillSize.Xsmall}
                  noTextTransform
                  theme={isOverridden() ? PillTheme.Orange : PillTheme.White}>
                  {isOverridden() ? l('_Overridden') : l('_AutoPopulatedFromPWERM')}
                </Pill>
              </div>
            )}
          </th>
        </tr>
        {expanded && (
          <>
            <tr>
              <th
                className="table-primary__cell--header table-primary__cell--header-primary"
                data-testid="control-premium-for-dloc-header">
                {l('_ControlPremiumForDloc')}
              </th>
              <td
                className="table-primary__cell--header table-primary__cell--header-primary"
                colSpan={maxDataColspanCount}
              />
            </tr>
            {sortedFilteredInstruments.map((instrument) => (
              <tr data-testid="dloc-row" key={`dloc-instrument-row-${instrument.instrumentId}`}>
                <th
                  className={`table-primary__cell--vertical-separator ${styles[`opm-table-rows`]}`}>
                  {instrument.instrumentNarrative}
                  {displayInstrumentDlocRevert(instrument.instrumentId) && (
                    <SvgRevert
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleInstrumentDlocRevert(instrument.instrumentId)}
                      className={styles['revert-button']}
                      data-testid={`${instrument.instrumentId}-dloc-revert-button`}
                    />
                  )}
                </th>
                <OpmInputEmptyHistoricCells indexIdentifier="controlPremiumForDloc" />
                <td>
                  <GridTextField
                    className={[
                      styles[`padding-right-index0`],
                      displayInstrumentDlocRevert(instrument.instrumentId)
                        ? styles['revertable-value']
                        : '',
                    ]}
                    name={`controlPremiumForDlocByInstrument.${instrument.instrumentId}`}
                    data-testid={`controlPremiumForDlocByInstrument.${instrument.instrumentId}`}
                    {...percentageFieldFormattingProps}
                  />
                </td>
                <td colSpan={maxColspanCount} />
              </tr>
            ))}
            <tr>
              <th
                className="table-primary__cell--header table-primary__cell--header-primary"
                data-testid="control-premium-for-dloc-header">
                {l('_DLOM')}
              </th>
              <td
                className="table-primary__cell--header table-primary__cell--header-primary"
                colSpan={maxDataColspanCount}
              />
            </tr>
            {sortedFilteredInstruments.map((instrument) => {
              return (
                <Fragment key={`instrument-row-fragment-${instrument.instrumentId}`}>
                  <tr data-testid="md-row" key={'instrument-row-wrapper' + instrument.instrumentId}>
                    <th
                      className={`table-primary__cell--vertical-separator ${
                        styles[`opm-table-rows`]
                      }`}>
                      {instrument.instrumentNarrative}
                      {displayInstrumentRevert(instrument.instrumentId) && (
                        <SvgRevert
                          // stop the onBlur event occurring so we can control when the data is submitted
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleInstrumentRevert(instrument.instrumentId)}
                          className={styles['revert-button']}
                          data-testid={`${instrument.instrumentId}-dlom-revert-button`}
                        />
                      )}
                    </th>
                    <OpmInputEmptyHistoricCells indexIdentifier="instrumentDlom" />
                    <td />
                    <Fragment key={`ordinary-equity-row-${instrument.instrumentId}`}>
                      {opmInput.perYearInputs.map((perYearInput, perYearInputIndex) => (
                        <td key={perYearInputIndex + 'ordinary-equity-value'}>
                          {Boolean(!instrument.payoutLogic?.length) && (
                            <GridTextField
                              name={`perYearInputs.${perYearInputIndex}.marketValueDloms.${getDlomIndexByInstrumentIdForOpm(
                                perYearInput,
                                instrument.instrumentId
                              )}.instrumentDlom`}
                              data-testid={`perYearInputs.${perYearInputIndex}.marketValueDloms.${getDlomIndexByInstrumentIdForOpm(
                                perYearInput,
                                instrument.instrumentId
                              )}.instrumentDlom`}
                              className={[
                                displayInstrumentRevertForYear(
                                  perYearInput,
                                  instrument.instrumentId
                                )
                                  ? styles['revertable-value']
                                  : '',
                              ]}
                              rules={{ ...minMaxValidator(0, 100) }}
                              {...percentageFieldFormattingProps}
                            />
                          )}
                        </td>
                      ))}
                    </Fragment>
                    <td colSpan={maxColspanCount} />
                  </tr>
                  <Fragment key={`ordinary-equity-row-${instrument.instrumentId}`}>
                    {instrument?.payoutLogic?.map((tranche, index) => (
                      <tr key={'instrument-row' + instrument.instrumentId + index}>
                        <Fragment key={tranche.id + 'tranche-row'}>
                          <th className="table-primary__cell--vertical-separator table-primary__cell--indentation">
                            {`Tranche ${index + 1}`}
                            {displayTrancheRevert(instrument.instrumentId, tranche.id) && (
                              <SvgRevert
                                // stop the onBlur event occurring so we can control when the data is submitted
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() =>
                                  handleTrancheRevert(instrument.instrumentId, tranche.id)
                                }
                                className={styles['revert-button']}
                                data-testid={`${tranche.id}-dlom-revert-button`}
                              />
                            )}
                          </th>
                          <OpmInputEmptyHistoricCells indexIdentifier="dlom" />
                          <td />
                          {opmInput.perYearInputs.map((perYearInput, perYearInputIndex) => (
                            <td key={tranche.id + perYearInput.forecastDate + 'value'}>
                              <GridTextField
                                name={`perYearInputs.${perYearInputIndex}.marketValueDloms.${getDlomIndexByInstrumentIdForOpm(
                                  perYearInput,
                                  instrument.instrumentId
                                )}.trancheDloms.${getDlomIndexByTrancheIdForOpm(
                                  perYearInput,
                                  tranche,
                                  instrument.instrumentId
                                )}.dlom`}
                                data-testid={`perYearInputs.${perYearInputIndex}.marketValueDloms.${getDlomIndexByInstrumentIdForOpm(
                                  perYearInput,
                                  instrument.instrumentId
                                )}.trancheDloms.${getDlomIndexByTrancheIdForOpm(
                                  perYearInput,
                                  tranche,
                                  instrument.instrumentId
                                )}.dlom`}
                                className={[
                                  displayTrancheRevertForYear(
                                    perYearInput,
                                    instrument.instrumentId,
                                    tranche.id
                                  )
                                    ? styles['revertable-value']
                                    : '',
                                ]}
                                rules={{ ...minMaxValidator(0, 100) }}
                                {...percentageFieldFormattingProps}
                              />
                            </td>
                          ))}
                          <td />
                        </Fragment>
                      </tr>
                    ))}
                  </Fragment>
                </Fragment>
              );
            })}
            {isOPMOnly && (
              <>
                <tr className="table-primary__row--secondary">
                  <th className="table-primary__cell--strong">{l('_MemoVolatility')}</th>
                  <td className="table-primary__cell--strong" colSpan={maxDataColspanCount} />
                </tr>
                <tr>
                  <th
                    className={`table-primary__cell--vertical-separator ${
                      styles[`opm-table-rows`]
                    }`}>
                    {l('_MarketVolatility')}
                  </th>
                  <td>
                    <GridTextField
                      className={[styles[`padding-right-index0`]]}
                      name="marketVolatility"
                      data-testid="marketVolatility"
                      {...percentageFieldFormattingProps}
                    />
                  </td>
                  <td colSpan={maxColspanCount} />
                </tr>
                <tr>
                  <th
                    className={`table-primary__cell--vertical-separator ${
                      styles[`opm-table-rows`]
                    }`}>
                    {l('_PeerSetVolatility')}
                  </th>
                  <td>
                    <GridTextField
                      className={[styles[`padding-right-index0`]]}
                      name="peerSetVolatility"
                      data-testid="peerSetVolatility"
                      {...percentageFieldFormattingProps}
                    />
                  </td>
                  <td />
                </tr>
              </>
            )}
          </>
        )}
      </tbody>
    </>
  ) : (
    <></>
  );
};

export default MinorityDiscountsSection;
